import InputError from "@/Components/UI/Forms/InputError";
import InputLabel from "@/Components/UI/Forms/InputLabel";
import classNames from "@/utils/classNames";
import useOutsideClick from "@/utils/useOutsideClick";
import { useEffect, useMemo, useRef, useState } from "react";
import { IoIosClose } from "react-icons/io";
import { IoChevronDown, IoSearch } from "react-icons/io5";

export type MultiSelectOption = {
    id: number | string;
    name: string;
};

type MultiSelectDropdownProps = {
    options: MultiSelectOption[];
    selectedOptions: MultiSelectOption[];
    onChange: (selected: MultiSelectOption[]) => void;
    label?: string;
    placeholder?: string;
    searchPlaceholder?: string;
    required?: boolean;
    error?: string;
    containerClassName?: string;
    testId?: string;
    enableSearch?: boolean;
    autoSelectByName?: string;
    pillClassNames?: string;
};

export default function MultiSelectDropdown({
    options,
    selectedOptions,
    onChange,
    label,
    placeholder = "Select options...",
    searchPlaceholder = "Search options...",
    required = false,
    error,
    containerClassName = "",
    testId = "multi-select-dropdown",
    enableSearch = true,
    autoSelectByName,
    pillClassNames = "",
}: MultiSelectDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [focusedOptionIndex, setFocusedOptionIndex] = useState(0);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useOutsideClick(dropdownRef, () => {
        if (isOpen) {
            setIsOpen(false);
            setSearchTerm("");
            setFocusedOptionIndex(0);
        }
    });

    const selectedIds = useMemo(
        () => new Set(selectedOptions.map((option) => option.id)),
        [selectedOptions],
    );

    // Auto-select an option by its name on mount (case-insensitive)
    useEffect(() => {
        if (!autoSelectByName) return;
        if (selectedOptions.length > 0) return;

        const match = options.find(
            (option) =>
                option.name.toLowerCase() === autoSelectByName.toLowerCase(),
        );
        if (match) {
            onChange([match]);
        }
    }, [autoSelectByName, options, selectedOptions.length, onChange]);

    const handleToggleOption = (option: MultiSelectOption) => {
        const isSelected = selectedIds.has(option.id);

        if (isSelected) {
            onChange(
                selectedOptions.filter((selected) => selected.id !== option.id),
            );
        } else {
            onChange([...selectedOptions, option]);
        }
    };

    const handleRemoveOption = (optionId: number | string) => {
        onChange(
            selectedOptions.filter((selected) => selected.id !== optionId),
        );
    };

    const filteredOptions = useMemo(() => {
        return options.filter((option) => {
            const matchesSearch = enableSearch
                ? option.name.toLowerCase().includes(searchTerm.toLowerCase())
                : true;
            const isNotSelected = !selectedIds.has(option.id);
            return matchesSearch && isNotSelected;
        });
    }, [options, enableSearch, searchTerm, selectedIds]);

    const handleKeyNavigation = (e: React.KeyboardEvent) => {
        if (!isOpen || filteredOptions.length === 0) return;

        const keyHandlers: Partial<Record<string, () => void>> = {
            ArrowDown: () =>
                setFocusedOptionIndex((prev) =>
                    prev < filteredOptions.length - 1 ? prev + 1 : 0,
                ),
            ArrowUp: () =>
                setFocusedOptionIndex((prev) =>
                    prev > 0 ? prev - 1 : filteredOptions.length - 1,
                ),
            Enter: () => {
                const option = filteredOptions[focusedOptionIndex];
                if (option) {
                    handleToggleOption(option);
                }
            },
            Escape: () => {
                setIsOpen(false);
                setSearchTerm("");
                setFocusedOptionIndex(0);
            },
        };

        const handler = keyHandlers[e.key];
        if (handler) {
            e.preventDefault();
            handler();
        }
    };

    const handleDropdownToggle = () => {
        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);
        if (!newIsOpen) {
            setSearchTerm("");
            setFocusedOptionIndex(0);
        } else if (enableSearch) {
            // Focus search input after DOM update
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 0);
        }
    };

    return (
        <div className={containerClassName}>
            {label && <InputLabel value={`${label}${required ? " *" : ""}`} />}

            <div className="relative" ref={dropdownRef}>
                <button
                    type="button"
                    className="w-full border border-brand-border rounded-lg px-3 py-2 bg-white cursor-pointer flex flex-wrap gap-2 items-center text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-red"
                    onClick={handleDropdownToggle}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleDropdownToggle();
                        } else if (isOpen) {
                            handleKeyNavigation(e);
                        }
                    }}
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    aria-label={label || "Multi-select dropdown"}
                    data-testid={
                        testId
                            ? `${testId}-multi-select-dropdown-button`
                            : undefined
                    }
                >
                    {selectedOptions.length > 0 ? (
                        selectedOptions.map((option) => (
                            <span
                                data-testid={`selected-option-${String(option.id).replace(/_/g, "-")}`}
                                key={option.id}
                                className={classNames(
                                    "inline-flex items-center bg-brand-red text-white px-3 py-0.5 rounded-full text-sm",
                                    pillClassNames,
                                )}
                            >
                                {option.name}
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveOption(option.id);
                                    }}
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === "Enter" ||
                                            e.key === " "
                                        ) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleRemoveOption(option.id);
                                        }
                                    }}
                                    className="ml-2 hover:text-brand-lightGray hover:bg-brand-gray cursor-pointer focus:outline-none focus:ring-1 focus:ring-white rounded"
                                    aria-label={`Remove ${option.name}`}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <IoIosClose className="w-4 h-4" />
                                </span>
                            </span>
                        ))
                    ) : (
                        <span className="text-brand-gray">{placeholder}</span>
                    )}

                    <IoChevronDown
                        className={`w-5 h-5 text-brand-gray ml-auto transition-transform ${
                            isOpen ? "rotate-180" : ""
                        }`}
                    />
                </button>

                {isOpen && (
                    <div
                        className="absolute top-full left-0 right-0 bg-white border border-brand-border rounded-lg mt-1 shadow-lg z-20"
                        data-testid={`${testId}-dropdown`}
                    >
                        {enableSearch && (
                            <div className="p-3 border-b border-brand-border">
                                <div className="relative">
                                    <IoSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-gray w-4 h-4" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) =>
                                            setSearchTerm(e.target.value)
                                        }
                                        placeholder={searchPlaceholder}
                                        className="w-full pl-10 pr-3 py-2 border border-brand-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-red"
                                        data-testid={
                                            testId
                                                ? `${testId}-search`
                                                : undefined
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === "ArrowDown" ||
                                                e.key === "ArrowUp" ||
                                                e.key === "Enter" ||
                                                e.key === "Escape"
                                            ) {
                                                handleKeyNavigation(e);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        <div
                            role="listbox"
                            className="max-h-48 overflow-y-auto"
                            aria-label="Available options"
                            data-testid={
                                testId ? `${testId}-options` : undefined
                            }
                        >
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option, index) => (
                                    <button
                                        key={option.id}
                                        type="button"
                                        role="option"
                                        aria-selected="false"
                                        className={`w-full text-brand-black text-left p-3 cursor-pointer focus:outline-none transition-colors ${
                                            index === focusedOptionIndex
                                                ? "bg-brand-lightGray"
                                                : "hover:bg-brand-lightGray"
                                        }`}
                                        onClick={() => {
                                            handleToggleOption(option);
                                        }}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === "Enter" ||
                                                e.key === " "
                                            ) {
                                                e.preventDefault();
                                                handleToggleOption(option);
                                            }
                                        }}
                                        data-testid={`dropdown-option-${String(option.id).replace(/_/g, "-")}`}
                                    >
                                        {option.name}
                                    </button>
                                ))
                            ) : (
                                <div className="p-3">
                                    <span className="text-brand-gray text-sm">
                                        {searchTerm
                                            ? "No options found"
                                            : "All options selected"}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {error && <InputError message={error} />}
        </div>
    );
}
